import { useState } from 'react';
import { ContractError } from './contract.js';
import { ApiProblem } from './problem.js';

/**
 * `Idempotency-Key` por intención del usuario (plan 11 §4.4):
 * - Un UUID nuevo por intención (p. ej., al pulsar «Enviar»).
 * - Si la misma petición se repite tras un **fallo de red** (no hubo respuesta), se reutiliza la clave.
 * - Si cambia el contenido, la clave cambia, para no recibir 422 `IDEMPOTENCY_KEY_REUSED`.
 * - Con una respuesta definitiva (éxito o error de la API), la clave se descarta.
 */
export class IntentKey {
  private key: string | undefined;
  private content: string | undefined;

  constructor(private readonly newKey: () => string = () => crypto.randomUUID()) {}

  /** Clave para este contenido: la pendiente si el contenido es el mismo, o una nueva. */
  keyFor(content: unknown): string {
    const fingerprint = JSON.stringify(content);
    if (this.key === undefined || this.content !== fingerprint) {
      this.key = this.newKey();
      this.content = fingerprint;
    }
    return this.key;
  }

  /** Tras la petición: se conserva solo si no hubo respuesta (fallo de red). */
  settle(error?: unknown): void {
    if (error === undefined || !isNetworkFailure(error)) {
      this.key = undefined;
      this.content = undefined;
    }
  }
}

/** Sin respuesta de la API: ni Problem Details ni una respuesta que no cumple el contrato. */
export function isNetworkFailure(error: unknown): boolean {
  return !(error instanceof ApiProblem) && !(error instanceof ContractError);
}

/** Ejecuta la mutación con la clave de la intención y la descarta (o la conserva) según el resultado. */
export async function withIntent<T>(intent: IntentKey, content: unknown, run: (key: string) => Promise<T>): Promise<T> {
  const key = intent.keyFor(content);
  try {
    const result = await run(key);
    intent.settle();
    return result;
  } catch (error) {
    intent.settle(error);
    throw error;
  }
}

/** Una `IntentKey` por componente (vive mientras la pantalla está abierta). */
export function useIntentKey(): IntentKey {
  const [intent] = useState(() => new IntentKey());
  return intent;
}

/** `If-Match` con la versión del recurso leído, entre comillas (`"3"`). */
export const ifMatch = (version: number) => `"${version}"`;
